// =============================================================================
// BP-10-04: Life Care Plan Editor Component
// BrightPath Platform - Full LCP Form with Cost Categories
// =============================================================================

import React, { useState, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Save,
  FileDown,
  Calculator,
  Plus,
  Stethoscope,
  Activity,
  Accessibility,
  Home,
  Heart,
  Car,
  GraduationCap,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { CostCategoryTable } from './CostCategoryTable';
import { LifetimeCostChart } from './LifetimeCostChart';
import type { LifeCarePlan, LifeCarePlanFormData, CostCategory } from '../types/attorney';
import { LifeCarePlanSchema, LifeCarePlanStatus } from '../types/attorney';
import { formatCurrency } from '@/lib/formatters';

// =============================================================================
// Types
// =============================================================================

interface LifeCarePlanEditorProps {
  lifeCarePlan?: LifeCarePlan;
  children: Array<{ id: string; firstName: string; lastName: string; dateOfBirth: string }>;
  onSave: (data: LifeCarePlanFormData) => void;
  onCalculate?: (data: LifeCarePlanFormData) => Promise<{
    annualCostCurrent: number;
    annualCostAge18: number;
    totalLifetimeCost: number;
  }>;
  onGenerateReport?: () => void;
  isLoading?: boolean;
  className?: string;
}

// =============================================================================
// Cost Category Configuration
// =============================================================================

const costCategories = [
  {
    key: 'medical' as const,
    label: 'Medical Care',
    icon: Stethoscope,
    color: 'bg-blue-100 text-blue-600',
    description: 'Physicians, specialists, hospitalizations, surgeries',
  },
  {
    key: 'therapy' as const,
    label: 'Therapies',
    icon: Activity,
    color: 'bg-purple-100 text-purple-600',
    description: 'PT, OT, Speech, ABA, behavioral therapy',
  },
  {
    key: 'equipment' as const,
    label: 'Equipment',
    icon: Accessibility,
    color: 'bg-orange-100 text-orange-600',
    description: 'Accessibilitys, orthotics, communication devices',
  },
  {
    key: 'homeModification' as const,
    label: 'Home Modifications',
    icon: Home,
    color: 'bg-teal-100 text-teal-600',
    description: 'Ramps, lifts, bathroom modifications',
  },
  {
    key: 'care' as const,
    label: 'Attendant Care',
    icon: Heart,
    color: 'bg-red-100 text-red-600',
    description: 'Home health aides, nursing care, respite',
  },
  {
    key: 'transportation' as const,
    label: 'Transportation',
    icon: Car,
    color: 'bg-amber-100 text-amber-600',
    description: 'Accessible vehicle, modifications, transport services',
  },
  {
    key: 'education' as const,
    label: 'Education',
    icon: GraduationCap,
    color: 'bg-sage-100 text-sage-600',
    description: 'Special education, tutoring, college accommodations',
  },
];

type CostCategoryKey = typeof costCategories[number]['key'];

// =============================================================================
// Component
// =============================================================================

export function LifeCarePlanEditor({
  lifeCarePlan,
  children,
  onSave,
  onCalculate,
  onGenerateReport,
  isLoading = false,
  className,
}: LifeCarePlanEditorProps) {
  const [activeTab, setActiveTab] = useState<CostCategoryKey | 'summary'>('medical');
  const [expandedCategories, setExpandedCategories] = useState<CostCategoryKey[]>(['medical']);
  const [calculatedTotals, setCalculatedTotals] = useState<{
    annualCostCurrent: number;
    annualCostAge18: number;
    totalLifetimeCost: number;
  } | null>(null);
  
  const isEditing = !!lifeCarePlan;
  
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<LifeCarePlanFormData>({
    resolver: zodResolver(LifeCarePlanSchema),
    defaultValues: {
      childId: lifeCarePlan?.childId || '',
      preparerName: lifeCarePlan?.preparerName || '',
      preparerCredentials: lifeCarePlan?.preparerCredentials || '',
      preparerOrganization: lifeCarePlan?.preparerOrganization || '',
      preparationDate: lifeCarePlan?.preparationDate || new Date().toISOString().split('T')[0],
      lifeExpectancyYears: lifeCarePlan?.lifeExpectancyYears || 70,
      lifeExpectancySource: lifeCarePlan?.lifeExpectancySource || '',
      medicalCosts: lifeCarePlan?.medicalCosts || [],
      therapyCosts: lifeCarePlan?.therapyCosts || [],
      equipmentCosts: lifeCarePlan?.equipmentCosts || [],
      homeModificationCosts: lifeCarePlan?.homeModificationCosts || [],
      careCosts: lifeCarePlan?.careCosts || [],
      transportationCosts: lifeCarePlan?.transportationCosts || [],
      educationCosts: lifeCarePlan?.educationCosts || [],
      assumptions: lifeCarePlan?.assumptions || '',
      methodology: lifeCarePlan?.methodology || '',
      status: lifeCarePlan?.status || 'draft',
    },
  });
  
  // Watch all cost arrays for totals
  const watchedCosts = {
    medical: watch('medicalCosts'),
    therapy: watch('therapyCosts'),
    equipment: watch('equipmentCosts'),
    homeModification: watch('homeModificationCosts'),
    care: watch('careCosts'),
    transportation: watch('transportationCosts'),
    education: watch('educationCosts'),
  };
  
  // Calculate local totals
  const localTotals = React.useMemo(() => {
    const byCategory: Record<string, number> = {};
    let total = 0;
    
    Object.entries(watchedCosts).forEach(([key, costs]) => {
      const categoryTotal = (costs || []).reduce((sum, c) => sum + (c.annualCost || 0), 0);
      byCategory[key] = categoryTotal;
      total += categoryTotal;
    });
    
    return { byCategory, total };
  }, [watchedCosts]);
  
  const toggleCategory = (key: CostCategoryKey) => {
    setExpandedCategories((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };
  
  const handleCalculate = async () => {
    if (!onCalculate) return;
    const formData = watch();
    const totals = await onCalculate(formData);
    setCalculatedTotals(totals);
  };
  
  return (
    <form
      onSubmit={handleSubmit(onSave)}
      className={cn('space-y-6', className)}
    >
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold text-navy">
            {isEditing ? 'Edit Life Care Plan' : 'New Life Care Plan'}
          </h2>
          {isDirty && (
            <p className="text-sm text-amber-600 flex items-center gap-1 mt-1">
              <AlertCircle className="w-4 h-4" />
              Unsaved changes
            </p>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {onCalculate && (
            <button
              type="button"
              onClick={handleCalculate}
              className="flex items-center gap-2 px-4 py-2 border border-sage text-sage rounded-lg hover:bg-sage-50 transition-colors"
            >
              <Calculator className="w-4 h-4" />
              Calculate Totals
            </button>
          )}
          {onGenerateReport && isEditing && (
            <button
              type="button"
              onClick={onGenerateReport}
              className="flex items-center gap-2 px-4 py-2 border border-navy text-navy rounded-lg hover:bg-navy-50 transition-colors"
            >
              <FileDown className="w-4 h-4" />
              Generate PDF
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              'flex items-center gap-2 px-6 py-2 bg-sage text-white rounded-lg',
              'hover:bg-sage-dark transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <Save className="w-4 h-4" />
            {isLoading ? 'Saving...' : 'Save Plan'}
          </button>
        </div>
      </div>
      
      {/* Basic Information */}
      <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
        <h3 className="font-display text-lg font-semibold text-navy mb-4">
          Plan Information
        </h3>
        
        <div className="grid grid-cols-2 gap-6">
          {/* Child Selection */}
          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Child <span className="text-red-500">*</span>
            </label>
            <select
              {...register('childId')}
              className={cn(
                'w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg',
                'focus:outline-none focus:border-sage transition-colors',
                errors.childId && 'border-red-300'
              )}
            >
              <option value="">Select a child...</option>
              {children.map((child) => (
                <option key={child.id} value={child.id}>
                  {child.firstName} {child.lastName}
                </option>
              ))}
            </select>
            {errors.childId && (
              <p className="mt-1 text-sm text-red-600">{errors.childId.message}</p>
            )}
          </div>
          
          {/* Preparation Date */}
          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Preparation Date
            </label>
            <input
              type="date"
              {...register('preparationDate')}
              className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage"
            />
          </div>
          
          {/* Preparer Name */}
          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Preparer Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('preparerName')}
              placeholder="Life care planner name"
              className={cn(
                'w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg',
                'focus:outline-none focus:border-sage',
                errors.preparerName && 'border-red-300'
              )}
            />
          </div>
          
          {/* Preparer Credentials */}
          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Credentials
            </label>
            <input
              type="text"
              {...register('preparerCredentials')}
              placeholder="RN, CLCP, CNLCP, etc."
              className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage"
            />
          </div>
          
          {/* Organization */}
          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Organization
            </label>
            <input
              type="text"
              {...register('preparerOrganization')}
              placeholder="Company or practice name"
              className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage"
            />
          </div>
          
          {/* Life Expectancy */}
          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Life Expectancy (years) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              {...register('lifeExpectancyYears', { valueAsNumber: true })}
              min={1}
              max={100}
              className={cn(
                'w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg',
                'focus:outline-none focus:border-sage',
                errors.lifeExpectancyYears && 'border-red-300'
              )}
            />
          </div>
          
          {/* Life Expectancy Source */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-navy mb-1">
              Life Expectancy Source
            </label>
            <input
              type="text"
              {...register('lifeExpectancySource')}
              placeholder="CDC life tables, expert opinion, etc."
              className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage"
            />
          </div>
          
          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Status
            </label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <div className="flex gap-2">
                  {(['draft', 'review', 'final'] as const).map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => field.onChange(status)}
                      className={cn(
                        'flex-1 py-2 px-4 rounded-lg border-2 font-medium text-sm capitalize',
                        field.value === status
                          ? 'border-sage bg-sage-50 text-sage-dark'
                          : 'border-warm-gray/20 text-warm-gray hover:border-sage/50'
                      )}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              )}
            />
          </div>
        </div>
      </div>
      
      {/* Cost Categories */}
      <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
        <div className="p-6 border-b border-warm-gray/10">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold text-navy">
              Cost Categories
            </h3>
            <div className="text-right">
              <p className="text-sm text-warm-gray">Total Annual Cost</p>
              <p className="font-display text-2xl font-bold text-sage">
                {formatCurrency(localTotals.total)}
              </p>
            </div>
          </div>
        </div>
        
        {/* Category Accordion */}
        <div className="divide-y divide-warm-gray/10">
          {costCategories.map((category) => {
            const Icon = category.icon;
            const isExpanded = expandedCategories.includes(category.key);
            const categoryTotal = localTotals.byCategory[category.key] || 0;
            const fieldName = `${category.key}Costs` as keyof LifeCarePlanFormData;
            
            return (
              <div key={category.key}>
                {/* Category Header */}
                <button
                  type="button"
                  onClick={() => toggleCategory(category.key)}
                  className="w-full flex items-center justify-between p-4 hover:bg-cream/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', category.color)}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                      <h4 className="font-medium text-navy">{category.label}</h4>
                      <p className="text-sm text-warm-gray">{category.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-semibold text-navy">
                      {formatCurrency(categoryTotal)}/yr
                    </span>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-warm-gray" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-warm-gray" />
                    )}
                  </div>
                </button>
                
                {/* Category Content */}
                {isExpanded && (
                  <div className="px-4 pb-4">
                    <Controller
                      name={fieldName as any}
                      control={control}
                      render={({ field }) => (
                        <CostCategoryTable
                          costs={field.value as CostCategory[]}
                          onChange={field.onChange}
                          categoryLabel={category.label}
                        />
                      )}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Calculated Totals Summary */}
      {calculatedTotals && (
        <div className="bg-sage-50 rounded-xl border border-sage/20 p-6">
          <h3 className="font-display text-lg font-semibold text-navy mb-4">
            Calculated Lifetime Costs
          </h3>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-warm-gray mb-1">Annual Cost (Current)</p>
              <p className="font-display text-2xl font-bold text-navy">
                {formatCurrency(calculatedTotals.annualCostCurrent)}
              </p>
            </div>
            <div>
              <p className="text-sm text-warm-gray mb-1">Annual Cost (Age 18+)</p>
              <p className="font-display text-2xl font-bold text-navy">
                {formatCurrency(calculatedTotals.annualCostAge18)}
              </p>
            </div>
            <div>
              <p className="text-sm text-warm-gray mb-1">Total Lifetime Cost</p>
              <p className="font-display text-3xl font-bold text-sage">
                {formatCurrency(calculatedTotals.totalLifetimeCost)}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Assumptions & Methodology */}
      <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
        <h3 className="font-display text-lg font-semibold text-navy mb-4">
          Assumptions & Methodology
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Assumptions
            </label>
            <textarea
              {...register('assumptions')}
              rows={4}
              placeholder="Document key assumptions used in this life care plan..."
              className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage resize-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Methodology
            </label>
            <textarea
              {...register('methodology')}
              rows={4}
              placeholder="Describe the methodology used for cost calculations..."
              className="w-full px-4 py-2.5 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage resize-none"
            />
          </div>
        </div>
      </div>
    </form>
  );
}

export default LifeCarePlanEditor;



